import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { formatIndianCurrency, formatIndianDate } from '../utils/format';
import ConfirmModal from './ConfirmModal';
import { useToast } from './ToastHost';

const Projects = ({ user }) => {
  const normalizedRole = String(user?.role || '').trim().toUpperCase();

  const splitNames = (s) => {
    if (!s || typeof s !== 'string') return [];
    return s
      .split(',')
      .map((x) => x.trim())
      .filter(Boolean);
  };

  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState('');
  const [selectedClientUserId, setSelectedClientUserId] = useState('');
  const [clientAssignSelection, setClientAssignSelection] = useState({});
  const [clientAssignBusy, setClientAssignBusy] = useState({});
  const [assignClientUserOpen, setAssignClientUserOpen] = useState(false);
  const [assignClientUserProject, setAssignClientUserProject] = useState(null);
  const [assignClientUserClientId, setAssignClientUserClientId] = useState('');
  const [assignClientUserUsers, setAssignClientUserUsers] = useState([]);
  const [assignClientUserId, setAssignClientUserId] = useState('');
  const [assignClientUserBusy, setAssignClientUserBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [total, setTotal] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({
    title: '',
    client_name: '',
    budget: '',
    quoted_amount: '',
    status: 'Lead',
    priority: 'MEDIUM',
    notes: ''
  });
  const [formData, setFormData] = useState({
    title: '',
    client_name: '',
    budget: '',
    quoted_amount: '',
    priority: 'MEDIUM',
    notes: ''
  });

  const [filters, setFilters] = useState({ q: '', status: '', priority: '', client_id: '', developer_id: '' });
  const [includeInactive, setIncludeInactive] = useState(false);

  const [devModalOpen, setDevModalOpen] = useState(false);
  const [devModalProject, setDevModalProject] = useState(null);
  const [devUsers, setDevUsers] = useState([]);
  const [selectedDevId, setSelectedDevId] = useState('');
  const [devBusy, setDevBusy] = useState(false);

  const [salesModalOpen, setSalesModalOpen] = useState(false);
  const [salesModalProject, setSalesModalProject] = useState(null);
  const [salesUsers, setSalesUsers] = useState([]);
  const [selectedSalesId, setSelectedSalesId] = useState('');
  const [salesBusy, setSalesBusy] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmMessage, setConfirmMessage] = useState('');
  const [confirmBusy, setConfirmBusy] = useState(false);
  const confirmActionRef = React.useRef(null);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsProject, setDetailsProject] = useState(null);

  const openConfirm = (title, message, action) => {
    confirmActionRef.current = action;
    setConfirmTitle(title);
    setConfirmMessage(message);
    setConfirmOpen(true);
  };

  const openDetails = (project) => {
    setDetailsProject(project);
    setDetailsOpen(true);
  };

  const closeDetails = () => {
    setDetailsOpen(false);
    setDetailsProject(null);
  };

  const openSalesModal = async (project) => {
    setSalesModalProject(project);
    setSalesModalOpen(true);
    setSelectedSalesId('');
    setSalesBusy(true);
    try {
      const res = await api.get('/api/users/sales-directory');
      setSalesUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error loading sales users:', error);
      setSalesUsers([]);
    } finally {
      setSalesBusy(false);
    }
  };

  const closeSalesModal = () => {
    setSalesModalOpen(false);
    setSalesModalProject(null);
    setSalesUsers([]);
    setSelectedSalesId('');
    setSalesBusy(false);
  };

  const requestSalesMapping = async () => {
    if (!salesModalProject || !selectedSalesId) return;
    setSalesBusy(true);
    try {
      await api.post('/api/bdm-sales-mapping', { sales_id: selectedSalesId });
      toast.success('Request sent to admin');
      closeSalesModal();
    } catch (error) {
      console.error('Error requesting sales mapping:', error);
      toast.error('Failed to submit request');
    } finally {
      setSalesBusy(false);
    }
  };

  useEffect(() => {
    fetchProjects(1);
  }, []);

  const fetchClients = useCallback(async () => {
    try {
      const res = await api.get('/api/clients');
      setClients(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    }
  }, []);

  const fetchDevelopers = useCallback(async () => {
    try {
      const res = await api.get('/api/users/developer-directory');
      setDevUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      setDevUsers([]);
    }
  }, []);

  useEffect(() => {
    if (user.role === 'ADMIN' || user.role === 'BDM') {
      fetchClients();
      fetchDevelopers();
    }
  }, [fetchClients, fetchDevelopers, user.role]);


  useEffect(() => {
    if (location.state?.openForm && (user.role === 'ADMIN' || user.role === 'BDM')) {
      setShowForm(true);
    }
  }, [location.state, user.role]);

  useEffect(() => {
    const editId = location.state?.editId;
    if (!editId) return;
    if (loading) return;
    if (!(user.role === 'ADMIN' || user.role === 'BDM')) return;

    const target = projects.find((p) => Number(p.id) === Number(editId));
    if (target) {
      startEdit(target);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, loading, projects, user.role]);

  const fetchProjects = async (nextPage = page) => {
    try {
      const isDeactivatedFilter = user.role === 'ADMIN' && filters.status === 'DEACTIVATED';
      const response = await api.get('/api/projects', {
        params: {
          page: nextPage,
          limit,
          q: filters.q || undefined,
          status: (filters.status && filters.status !== 'DEACTIVATED') ? filters.status : undefined,
          priority: filters.priority || undefined,
          client_id: filters.client_id || undefined,
          developer_id: filters.developer_id || undefined,
          include_inactive: isDeactivatedFilter ? true : (user.role === 'ADMIN' && includeInactive ? true : undefined)
        }
      });
      const payload = response.data;
      let projectList = [];
      if (payload && typeof payload === 'object' && Array.isArray(payload.items)) {
        projectList = payload.items;
        setTotal(payload.total || 0);
        setPage(payload.page || nextPage);
      } else {
        projectList = Array.isArray(payload) ? payload : [];
        setTotal(Array.isArray(payload) ? payload.length : 0);
        setPage(nextPage);
      }
      // For DEACTIVATED filter, show only is_active=0 projects
      if (isDeactivatedFilter) {
        projectList = projectList.filter((p) => Number(p.is_active) === 0);
        setTotal(projectList.length);
      }
      setProjects(projectList);
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q, filters.status, filters.priority, filters.client_id, filters.developer_id, includeInactive]);

  const assignClientToProject = async (projectId) => {
    const selected = clientAssignSelection[projectId];
    setClientAssignBusy((prev) => ({ ...prev, [projectId]: true }));
    try {
      await api.post(`/api/projects/${projectId}/assign-client`, { client_id: selected ? Number(selected) : null });
      await fetchProjects(page);
    } catch (error) {
      console.error('Error assigning client:', error);
    } finally {
      setClientAssignBusy((prev) => ({ ...prev, [projectId]: false }));
    }
  };

  const openAssignClientUserModal = async (project) => {
    setAssignClientUserProject(project);
    setAssignClientUserOpen(true);

    const cid = project?.client_id ? String(project.client_id) : '';
    setAssignClientUserClientId(cid);
    setAssignClientUserUsers([]);
    setAssignClientUserId(project?.assigned_client_user_id ? String(project.assigned_client_user_id) : '');

    if (!cid) return;
    setAssignClientUserBusy(true);
    try {
      const res = await api.get(`/api/clients/${cid}/users`);
      setAssignClientUserUsers(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setAssignClientUserUsers([]);
    } finally {
      setAssignClientUserBusy(false);
    }
  };

  const closeAssignClientUserModal = () => {
    setAssignClientUserOpen(false);
    setAssignClientUserProject(null);
    setAssignClientUserClientId('');
    setAssignClientUserUsers([]);
    setAssignClientUserId('');
    setAssignClientUserBusy(false);
  };

  const saveAssignedClientUser = async () => {
    if (!assignClientUserProject) return;
    if (!assignClientUserClientId || !assignClientUserId) {
      toast.error('Select client company and client user');
      return;
    }
    setAssignClientUserBusy(true);
    try {
      await api.post(`/api/projects/${assignClientUserProject.id}/assign-client`, {
        client_id: Number(assignClientUserClientId),
        assigned_client_user_id: Number(assignClientUserId)
      });
      toast.success('Client user assigned');
      closeAssignClientUserModal();
      await fetchProjects(page);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to assign client user');
    } finally {
      setAssignClientUserBusy(false);
    }
  };

  const openDevModal = async (project) => {
    setDevModalProject(project);
    setDevModalOpen(true);
    setSelectedDevId('');
    setDevBusy(true);
    try {
      const [devListRes] = await Promise.all([
        api.get('/api/users/developer-directory')
      ]);
      setDevUsers(Array.isArray(devListRes.data) ? devListRes.data : []);
    } catch (error) {
      console.error('Error loading developers:', error);
      setDevUsers([]);
    } finally {
      setDevBusy(false);
    }
  };

  const closeDevModal = () => {
    setDevModalOpen(false);
    setDevModalProject(null);
    setDevUsers([]);
    setSelectedDevId('');
    setDevBusy(false);
  };

  const assignDeveloper = async () => {
    if (!devModalProject || !selectedDevId) return;
    setDevBusy(true);
    try {
      await api.post(`/api/projects/${devModalProject.id}/developers`, { developer_id: selectedDevId });
      toast.success('Developer assigned');
      setSelectedDevId('');
      closeDevModal();
    } catch (error) {
      console.error('Error assigning developer:', error);
      toast.error('Failed to assign developer');
    } finally {
      setDevBusy(false);
    }
  };

  const statusOptions = useMemo(
    () => ['Lead', 'Converted', 'Won', 'Discussion', 'Proposal', 'PO', 'Working', 'OnHold', 'Completed', 'Dropped'],
    []
  );

  const startEdit = (project) => {
    setEditingId(project.id);
    setEditData({
      title: project.title || '',
      client_name: project.client_name || '',
      budget: project.budget ?? '',
      quoted_amount: project.quoted_amount ?? '',
      status: project.status || 'Lead',
      priority: project.priority || 'MEDIUM',
      notes: project.notes || ''
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
  };

  const handleEditChange = (e) => {
    setEditData({
      ...editData,
      [e.target.name]: e.target.value
    });
  };

  const saveEdit = async (projectId) => {
    try {
      await api.put(`/api/projects/${projectId}`, {
        ...editData,
        budget: editData.budget === '' ? null : editData.budget,
        quoted_amount: editData.quoted_amount === '' ? null : editData.quoted_amount
      });
      setEditingId(null);
      fetchProjects();
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const deactivateProject = async (projectId) => {
    openConfirm('Deactivate project', 'Deactivate this project? It will be hidden from the system.', async () => {
      try {
        await api.put(`/api/projects/${projectId}`, { is_active: 0 });
        await fetchProjects(1);
        toast.success('Project deactivated');
      } catch (error) {
        console.error('Error deleting project:', error);
        toast.error('Failed to deactivate project');
      }
    });
  };

  const activateProject = async (projectId) => {
    openConfirm('Activate project', 'Activate this project and make it visible again?', async () => {
      try {
        await api.put(`/api/projects/${projectId}`, { is_active: 1 });
        await fetchProjects(1);
        toast.success('Project activated');
      } catch (error) {
        console.error('Error activating project:', error);
        toast.error('Failed to activate project');
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (user.role === 'SALES') {
        await api.post('/api/project-requests', formData);
        toast.success('Project request submitted');
      } else {
        await api.post('/api/projects', {
          ...formData,
          client_id: selectedClientId ? Number(selectedClientId) : null,
          assigned_client_user_id: selectedClientUserId ? Number(selectedClientUserId) : null
        });
        toast.success('Project created');
      }
      setShowForm(false);
      setFormData({
        title: '',
        client_name: '',
        budget: '',
        quoted_amount: '',
        priority: 'MEDIUM',
        notes: ''
      });
      setSelectedClientId('');
      setSelectedClientUserId('');
      setClientUsers([]);
      if (user.role !== 'SALES') {
        fetchProjects(1);
      } else {
        navigate('/project-requests');
      }
    } catch (error) {
      console.error('Error creating project:', error);
      toast.error('Failed to submit');
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const missingClientUserAssignments = useMemo(() => {
    if (!(user.role === 'ADMIN' || user.role === 'BDM')) return [];
    return (projects || []).filter((p) => p.client_id && !p.assigned_client_user_id);
  }, [projects, user.role]);

  if (loading) {
    return <div className="text-center p-8">Loading projects...</div>;
  }

  const totalPages = Math.max(Math.ceil(total / limit) || 1, 1);
  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h1 className="text-2xl font-bold">Projects</h1>

        <div className="flex gap-2">
          {(normalizedRole === 'ADMIN' || normalizedRole === 'BDM') && (
            <button
              onClick={() => setShowForm(true)}
              className="glass-button primary"
            >
              + New Project
            </button>
          )}
          {normalizedRole === 'SALES' && (
            <button
              onClick={() => setShowForm(true)}
              className="glass-button primary"
            >
              Request Project
            </button>
          )}
        </div>
      </div>

      <div className="filters-row" style={{ marginBottom: 18 }}>
        <input
          className="glass-input"
          placeholder="Search..."
          value={filters.q}
          onChange={(e) => setFilters((prev) => ({ ...prev, q: e.target.value }))}
        />
        <select
          className="glass-select"
          value={filters.status}
          onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}
        >
          <option value="">Status</option>
          {statusOptions.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
          {user.role === 'ADMIN' ? (
            <option value="DEACTIVATED">Deactivated</option>
          ) : null}
        </select>
        <select
          className="glass-select"
          value={filters.client_id}
          onChange={(e) => setFilters((prev) => ({ ...prev, client_id: e.target.value }))}
          disabled={!(user.role === 'ADMIN' || user.role === 'BDM')}
        >
          <option value="">Client</option>
          {clients.filter((c) => c.is_active === 1 || c.is_active === true).map((c) => (
            <option key={c.id} value={c.id}>{c.company || c.name}</option>
          ))}
        </select>
        <select
          className="glass-select"
          value={filters.developer_id}
          onChange={(e) => setFilters((prev) => ({ ...prev, developer_id: e.target.value }))}
          disabled={!(user.role === 'ADMIN' || user.role === 'BDM')}
        >
          <option value="">Developer</option>
          {devUsers.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>
        <select
          className="glass-select"
          value={filters.priority}
          onChange={(e) => setFilters((prev) => ({ ...prev, priority: e.target.value }))}
        >
          <option value="">Priority</option>
          <option value="LOW">Low</option>
          <option value="MEDIUM">Medium</option>
          <option value="HIGH">High</option>
        </select>
        <button
          type="button"
          className="glass-button"
          onClick={() => setFilters({ q: '', status: '', priority: '', client_id: '', developer_id: '' })}
          disabled={confirmBusy}
        >
          Reset
        </button>

        {user.role === 'ADMIN' ? (
          <label className="text-sm" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              disabled={confirmBusy}
            />
            Show Deactivated
          </label>
        ) : null}
      </div>

      {(normalizedRole === 'ADMIN' || normalizedRole === 'BDM') && missingClientUserAssignments.length > 0 ? (
        <div className="glass-card p-6 mb-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold">Missing Client User Assignment</h2>
              <div className="text-sm text-tertiary">Legacy projects need a specific client user to unlock client dashboards/issues.</div>
            </div>
            <div className="text-sm text-tertiary">{missingClientUserAssignments.length} project(s)</div>
          </div>

          <div className="grid gap-2 mt-4">
            {missingClientUserAssignments.slice(0, 12).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-3 p-3 rounded-lg" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div className="min-w-0">
                  <div className="font-medium truncate">{p.title}</div>
                  <div className="text-xs text-tertiary">Client: {p.client_name || '—'}</div>
                </div>
                <button type="button" className="glass-button text-sm px-3 py-1" onClick={() => openAssignClientUserModal(p)}>
                  Assign Client User
                </button>
              </div>
            ))}
          </div>

          {missingClientUserAssignments.length > 12 ? (
            <div className="text-xs text-tertiary mt-3">Showing first 12. Use search/filters (or paginate) to reach others.</div>
          ) : null}
        </div>
      ) : null}

      {showForm && (
        <div className="glass-card p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">{user.role === 'SALES' ? 'Request New Project' : 'Create New Project'}</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Title</label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className="glass-input w-full"
                required
              />
            </div>

            {user.role === 'SALES' ? (
              <div>
                <label className="block text-sm font-medium mb-2">Client Name</label>
                <input
                  type="text"
                  name="client_name"
                  value={formData.client_name}
                  onChange={handleChange}
                  className="glass-input w-full"
                  required
                />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-2">Client Company</label>
                <select
                  value={selectedClientId}
                  onChange={async (e) => {
                    const v = e.target.value;
                    setSelectedClientId(v);
                    setSelectedClientUserId('');
                    setClientUsers([]);

                    const selected = clients.find((c) => String(c.id) === String(v));
                    setFormData((prev) => ({ ...prev, client_name: selected?.company || selected?.name || '' }));

                    if (!v) return;
                    try {
                      const res = await api.get(`/api/clients/${v}/users`);
                      setClientUsers(Array.isArray(res.data) ? res.data : []);
                    } catch (err) {
                      setClientUsers([]);
                    }
                  }}
                  className="glass-select w-full"
                  required
                >
                  <option value="">Select company</option>
                  {clients.filter((c) => c.is_active === 1 || c.is_active === true).map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.company || c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {user.role === 'SALES' ? null : (
              <div>
                <label className="block text-sm font-medium mb-2">Client User</label>
                <select
                  value={selectedClientUserId}
                  onChange={(e) => setSelectedClientUserId(e.target.value)}
                  className="glass-select w-full"
                  disabled={!selectedClientId}
                  required
                >
                  <option value="">Select user</option>
                  {clientUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-2">Budget</label>
              <input
                type="number"
                name="budget"
                value={formData.budget}
                onChange={handleChange}
                className="glass-input w-full"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Quoted Amount</label>
              <input
                type="number"
                name="quoted_amount"
                value={formData.quoted_amount}
                onChange={handleChange}
                className="glass-input w-full"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="glass-select w-full"
              >
                <option value="LOW">Low</option>
                <option value="MEDIUM">Medium</option>
                <option value="HIGH">High</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-2">Notes</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                className="glass-textarea w-full"
                rows="3"
              />
            </div>

            <div className="col-span-2 flex gap-2">
              <button type="submit" className="glass-button primary">
                {user.role === 'SALES' ? 'Submit Request' : 'Create Project'}
              </button>
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="glass-button"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid gap-4">
        {projects.map((project) => (
          <div
            key={project.id}
            className="glass-card p-6"
            role="button"
            tabIndex={0}
            onClick={() => {
              if (editingId === project.id) return;
              openDetails(project);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                if (editingId === project.id) return;
                openDetails(project);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="flex justify-between items-start mb-4">
              <div>
                <h3 className="text-lg font-semibold">{project.title}</h3>
                <p className="text-secondary">Client: {project.client_name}</p>
                {project.owner_name ? <p className="text-sm text-tertiary">Owner: {project.owner_name}</p> : null}

                {project.assigned_client_user_name || project.sales_names || project.developer_names ? (
                  <div className="project-pills mt-2">
                    {project.assigned_client_user_name ? (
                      <span className="name-pill pill-client">{project.assigned_client_user_name}</span>
                    ) : null}
                    {splitNames(project.sales_names).map((n) => (
                      <span key={`sales-${project.id}-${n}`} className="name-pill pill-sales">{n}</span>
                    ))}
                    {splitNames(project.developer_names).map((n) => (
                      <span key={`dev-${project.id}-${n}`} className="name-pill pill-dev">{n}</span>
                    ))}
                  </div>
                ) : null}

                <p className="text-sm text-tertiary">Created: {formatIndianDate(project.created_at)}</p>
              </div>
              <div className="flex gap-2">
                <span className={`status-badge status-${project.status.toLowerCase()}`}>
                  {project.status}
                </span>
                <span className={`status-badge priority-${project.priority.toLowerCase()}`}>
                  {project.priority}
                </span>
              </div>
            </div>

            {user.role !== 'DEVELOPER' && user.role !== 'CLIENT' && (
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <span className="text-sm text-secondary">Budget:</span>
                  <span className="ml-2 font-medium">
                    {project.budget !== null && project.budget !== undefined && project.budget !== ''
                      ? formatIndianCurrency(project.budget)
                      : 'Not set'}
                  </span>
                </div>
                <div>
                  <span className="text-sm text-secondary">Quoted:</span>
                  <span className="ml-2 font-medium">
                    {project.quoted_amount !== null && project.quoted_amount !== undefined && project.quoted_amount !== ''
                      ? formatIndianCurrency(project.quoted_amount)
                      : 'Not quoted'}
                  </span>
                </div>
              </div>
            )}

            {project.notes && (
              <div className="mb-4">
                <p className="text-sm text-secondary mb-1">Notes:</p>
                <p className="text-sm">{project.notes}</p>
              </div>
            )}


            {editingId === project.id ? (
              <div
                className="mt-4"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => e.stopPropagation()}
                role="presentation"
              >
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Title</label>
                    <input
                      type="text"
                      name="title"
                      value={editData.title}
                      onChange={handleEditChange}
                      className="glass-input w-full"
                      required
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Client Name</label>
                    <input
                      type="text"
                      name="client_name"
                      value={editData.client_name}
                      onChange={handleEditChange}
                      className="glass-input w-full"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Status</label>
                    <select name="status" value={editData.status} onChange={handleEditChange} className="glass-select w-full">
                      {statusOptions.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Priority</label>
                    <select name="priority" value={editData.priority} onChange={handleEditChange} className="glass-select w-full">
                      <option value="LOW">Low</option>
                      <option value="MEDIUM">Medium</option>
                      <option value="HIGH">High</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Budget</label>
                    <input type="number" name="budget" value={editData.budget} onChange={handleEditChange} className="glass-input w-full" step="0.01" />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Quoted Amount</label>
                    <input type="number" name="quoted_amount" value={editData.quoted_amount} onChange={handleEditChange} className="glass-input w-full" step="0.01" />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium mb-2">Notes</label>
                    <textarea name="notes" value={editData.notes} onChange={handleEditChange} className="glass-textarea w-full" rows="3" />
                  </div>
                </div>

                <div className="flex gap-2 mt-4">
                  <button type="button" className="glass-button primary" onClick={() => saveEdit(project.id)}>
                    Save
                  </button>
                  <button type="button" className="glass-button" onClick={cancelEdit}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-xs text-tertiary">
                  Created: {formatIndianDate(project.created_at)}
                </span>
                <div className="flex gap-2">
                  {(user.role === 'ADMIN' || user.role === 'BDM') && (
                    <>
                      <select
                        className="glass-select text-sm"
                        value={clientAssignSelection[project.id] ?? (project.client_id ? String(project.client_id) : '')}
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          e.stopPropagation();
                          setClientAssignSelection((prev) => ({ ...prev, [project.id]: e.target.value }));
                        }}
                        disabled={clientAssignBusy[project.id]}
                      >
                        <option value="">No Client</option>
                        {clients.filter((c) => c.is_active === 1 || c.is_active === true).map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      <button
                        className="glass-button text-sm px-3 py-1"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          assignClientToProject(project.id);
                        }}
                        disabled={clientAssignBusy[project.id]}
                      >
                        {clientAssignBusy[project.id] ? 'Assigning...' : 'Assign Client'}
                      </button>
                    </>
                  )}
                  {(user.role === 'ADMIN' || user.role === 'BDM') && (
                    <>
                      <button
                        className="glass-button text-sm px-3 py-1"
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          startEdit(project);
                        }}
                        disabled={confirmBusy}
                      >
                        Edit
                      </button>
                      {Number(project.is_active) === 0 ? (
                        user.role === 'ADMIN' ? (
                          <button
                            className="glass-button text-sm px-3 py-1"
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              activateProject(project.id);
                            }}
                            disabled={confirmBusy}
                          >
                            Activate
                          </button>
                        ) : null
                      ) : (
                        <button
                          className="glass-button danger text-sm px-3 py-1"
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            deactivateProject(project.id);
                          }}
                          disabled={confirmBusy}
                        >
                          Deactivate
                        </button>
                      )}
                    </>
                  )}
                  {user.role !== 'CLIENT' && (
                    <button
                      className="glass-button text-sm px-3 py-1"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/tasks?projectId=${project.id}`);
                      }}
                    >
                      View Tasks
                    </button>
                  )}
                  {user.role === 'ADMIN' && (
                    <button className="glass-button text-sm px-3 py-1" type="button" onClick={(e) => { e.stopPropagation(); openDevModal(project); }}>
                      Assign Developer
                    </button>
                  )}
                  {user.role === 'BDM' && Number(project.owner_id) === Number(user.id) && (
                    <button className="glass-button text-sm px-3 py-1" type="button" onClick={(e) => { e.stopPropagation(); openDevModal(project); }}>
                      Assign Developer
                    </button>
                  )}
                  {user.role === 'BDM' && Number(project.owner_id) === Number(user.id) && (
                    <button className="glass-button text-sm px-3 py-1" type="button" onClick={(e) => { e.stopPropagation(); openSalesModal(project); }}>
                      Assign Sales
                    </button>
                  )}
                  {user.role !== 'CLIENT' && (
                    <button
                      className="glass-button text-sm px-3 py-1"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/pmb/${project.id}`);
                      }}
                    >
                      PMB
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {detailsOpen && detailsProject ? (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeDetails}>
          <div className="modal" style={{ width: 'min(980px, 100%)' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Project Details</h3>
              <button type="button" className="glass-button" onClick={closeDetails}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="grid grid-cols-2 gap-4" style={{ marginBottom: 14 }}>
                <div className="col-span-2">
                  <div className="text-xl font-semibold">{detailsProject.title}</div>
                  <div className="text-sm text-secondary">Client: {detailsProject.client_name || '—'}</div>
                </div>

                <div className="glass-card p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="text-sm font-semibold mb-2">Status</div>
                  <div className="flex gap-2 flex-wrap">
                    {detailsProject.status ? (
                      <span className={`status-badge status-${String(detailsProject.status).toLowerCase()}`}>{detailsProject.status}</span>
                    ) : null}
                    {detailsProject.priority ? (
                      <span className={`status-badge priority-${String(detailsProject.priority).toLowerCase()}`}>{detailsProject.priority}</span>
                    ) : null}
                  </div>
                  <div className="text-xs text-tertiary" style={{ marginTop: 10 }}>Created: {formatIndianDate(detailsProject.created_at)}</div>
                </div>

                <div className="glass-card p-4" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="text-sm font-semibold mb-2">Commercials</div>
                  <div className="text-sm">
                    <div>
                      <span className="text-secondary">Budget:</span>
                      <span className="ml-2 font-medium">
                        {detailsProject.budget !== null && detailsProject.budget !== undefined && detailsProject.budget !== ''
                          ? formatIndianCurrency(detailsProject.budget)
                          : 'Not set'}
                      </span>
                    </div>
                    <div style={{ marginTop: 6 }}>
                      <span className="text-secondary">Quoted:</span>
                      <span className="ml-2 font-medium">
                        {detailsProject.quoted_amount !== null && detailsProject.quoted_amount !== undefined && detailsProject.quoted_amount !== ''
                          ? formatIndianCurrency(detailsProject.quoted_amount)
                          : 'Not quoted'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="glass-card p-4 col-span-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
                  <div className="text-sm font-semibold mb-2">People</div>
                  <div className="project-pills">
                    {detailsProject.assigned_client_user_name ? (
                      <span className="name-pill pill-client">Client user: {detailsProject.assigned_client_user_name}</span>
                    ) : null}
                    {splitNames(detailsProject.sales_names).map((n) => (
                      <span key={`details-sales-${detailsProject.id}-${n}`} className="name-pill pill-sales">Sales: {n}</span>
                    ))}
                    {splitNames(detailsProject.developer_names).map((n) => (
                      <span key={`details-dev-${detailsProject.id}-${n}`} className="name-pill pill-dev">Dev: {n}</span>
                    ))}
                    {detailsProject.owner_name ? (
                      <span className="name-pill">Owner: {detailsProject.owner_name}</span>
                    ) : null}
                  </div>
                  {detailsProject.owner_email ? (
                    <div className="text-xs text-tertiary" style={{ marginTop: 8 }}>Owner email: {detailsProject.owner_email}</div>
                  ) : null}
                </div>

                {detailsProject.notes ? (
                  <div className="glass-card p-4 col-span-2" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    <div className="text-sm font-semibold mb-2">Notes</div>
                    <div className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{detailsProject.notes}</div>
                  </div>
                ) : null}
              </div>

              <div className="modal-footer">
                {(user.role === 'ADMIN' || user.role === 'BDM') ? (
                  <button
                    type="button"
                    className="glass-button"
                    onClick={() => {
                      closeDetails();
                      navigate(`/projects/${detailsProject.id}`);
                    }}
                  >
                    Overview
                  </button>
                ) : null}
                {user.role !== 'CLIENT' ? (
                  <button type="button" className="glass-button" onClick={() => navigate(`/tasks?projectId=${detailsProject.id}`)}>
                    View Tasks
                  </button>
                ) : null}
                {user.role !== 'CLIENT' ? (
                  <button type="button" className="glass-button" onClick={() => navigate(`/pmb/${detailsProject.id}`)}>
                    PMB
                  </button>
                ) : null}
                <button type="button" className="glass-button" onClick={closeDetails}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <div className="text-sm text-tertiary">
            Page {page} of {totalPages} ({total} projects)
          </div>
          <div className="flex gap-2">
            <button type="button" className="glass-button" onClick={() => fetchProjects(page - 1)} disabled={!canPrev}>
              Prev
            </button>
            <button type="button" className="glass-button" onClick={() => fetchProjects(page + 1)} disabled={!canNext}>
              Next
            </button>
          </div>
        </div>
      )}

      {projects.length === 0 && (
        <div className="text-center p-8 glass-card">
          <p className="text-secondary">No projects found</p>
        </div>
      )}

      {devModalOpen && devModalProject ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(560px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Assign Developer</h3>
              <button type="button" className="glass-button" onClick={closeDevModal} disabled={devBusy}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="text-secondary" style={{ marginBottom: 10 }}>
                {devModalProject.title}
              </div>
              <label className="block text-sm font-medium mb-2">Developer</label>
              <select
                className="glass-select w-full"
                value={selectedDevId}
                onChange={(e) => setSelectedDevId(e.target.value)}
                disabled={devBusy}
              >
                <option value="">Select developer</option>
                {devUsers.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name} ({d.email})
                  </option>
                ))}
              </select>
            </div>

            <div className="modal-footer">
              <button type="button" className="glass-button" onClick={closeDevModal} disabled={devBusy}>
                Cancel
              </button>
              <button type="button" className="glass-button primary" onClick={assignDeveloper} disabled={devBusy || !selectedDevId}>
                {devBusy ? 'Please wait...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {salesModalOpen && salesModalProject ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(560px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Assign Sales</h3>
              <button type="button" className="glass-button" onClick={closeSalesModal} disabled={salesBusy}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="text-secondary" style={{ marginBottom: 10 }}>
                {salesModalProject.title}
              </div>
              <label className="block text-sm font-medium mb-2">Sales person</label>
              <select
                className="glass-select w-full"
                value={selectedSalesId}
                onChange={(e) => setSelectedSalesId(e.target.value)}
                disabled={salesBusy}
              >
                <option value="">Select sales</option>
                {salesUsers.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name} ({s.email})
                  </option>
                ))}
              </select>
              <div className="text-xs text-tertiary" style={{ marginTop: 8 }}>
                This will be sent to Admin for approval.
              </div>
            </div>

            <div className="modal-footer">
              <button type="button" className="glass-button" onClick={closeSalesModal} disabled={salesBusy}>
                Cancel
              </button>
              <button
                type="button"
                className="glass-button primary"
                onClick={requestSalesMapping}
                disabled={salesBusy || !selectedSalesId}
              >
                {salesBusy ? 'Please wait...' : 'Add to Team'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmModal
        open={confirmOpen}
        title={confirmTitle}
        message={confirmMessage}
        confirmText="Confirm"
        cancelText="Cancel"
        busy={confirmBusy}
        onCancel={() => {
          if (confirmBusy) return;
          setConfirmOpen(false);
        }}
        onConfirm={async () => {
          const action = confirmActionRef.current;
          if (!action) {
            setConfirmOpen(false);
            return;
          }
          try {
            setConfirmBusy(true);
            await action();
            setConfirmOpen(false);
          } finally {
            setConfirmBusy(false);
          }
        }}
      />

      {assignClientUserOpen ? (
        <div className="modal-overlay" role="dialog" aria-modal="true">
          <div className="modal" style={{ width: 'min(860px, 100%)' }}>
            <div className="modal-header">
              <h3 className="text-lg font-semibold">Assign Client User</h3>
              <button type="button" className="glass-button" onClick={closeAssignClientUserModal} disabled={assignClientUserBusy}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div className="text-sm text-tertiary">
                Project: <span className="font-semibold">{assignClientUserProject?.title || ''}</span>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Client Company</label>
                  <select
                    className="glass-select w-full"
                    value={assignClientUserClientId}
                    onChange={async (e) => {
                      const v = e.target.value;
                      setAssignClientUserClientId(v);
                      setAssignClientUserId('');
                      setAssignClientUserUsers([]);
                      if (!v) return;
                      setAssignClientUserBusy(true);
                      try {
                        const res = await api.get(`/api/clients/${v}/users`);
                        setAssignClientUserUsers(Array.isArray(res.data) ? res.data : []);
                      } finally {
                        setAssignClientUserBusy(false);
                      }
                    }}
                    disabled={assignClientUserBusy}
                  >
                    <option value="">Select company</option>
                    {clients.filter((c) => c.is_active === 1 || c.is_active === true).map((c) => (
                      <option key={c.id} value={c.id}>{c.company || c.name}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Client User</label>
                  <select
                    className="glass-select w-full"
                    value={assignClientUserId}
                    onChange={(e) => setAssignClientUserId(e.target.value)}
                    disabled={!assignClientUserClientId || assignClientUserBusy}
                  >
                    <option value="">Select user</option>
                    {assignClientUserUsers.map((u) => (
                      <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="glass-button" onClick={closeAssignClientUserModal} disabled={assignClientUserBusy}>
                  Cancel
                </button>
                <button type="button" className="glass-button primary" onClick={saveAssignedClientUser} disabled={assignClientUserBusy}>
                  {assignClientUserBusy ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Projects;
